import { Loader2 } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { landingPathFor } from '@/lib/landing';
import { LandingPage } from '@/modules/marketing/LandingPage';
import { useAuthStore } from '@/stores/auth-store';

/**
 * `/` is the public front door; signed-in visitors are sent on to the application.
 *
 * Before this existed, `/` was the dashboard and an unauthenticated visitor was redirected to the
 * login form — so the product had no public page at all. Putting the landing page here and moving
 * the dashboard to `/dashboard` is the arrangement that serves both: a stranger gets something to
 * read, and a customer gets one redirect they never see.
 *
 * Session restore is waited for. Without it a returning customer sees the marketing page flash
 * before being moved on, which reads as having been signed out.
 *
 * Where "on to the application" leads depends on who is asking — see {@link landingPathFor}.
 */
export function HomeRoute() {
  const { user, isAuthenticated, bootstrapped } = useAuth();
  const impersonating = useAuthStore((s) => s.impersonation !== null);
  // Read from storage synchronously when the store is created, so it is known on the first render.
  const mayHaveSession = useAuthStore((s) => s.refreshToken !== null);

  // Only a visitor who might be signed in waits for the session to be restored. Everybody else is
  // shown the landing page at once: the build writes that page's markup into the HTML, and a
  // spinner on the first render would replace readable content with a blank screen until the
  // check finished.
  if (!bootstrapped && mayHaveSession) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LandingPage />;
  }

  return <Navigate to={landingPathFor(user, { impersonating })} replace />;
}
